import { useState, useRef, useEffect } from 'react'
import { pricingService } from '@/services/pricing-service'

/* ── Load SheetJS from CDN ── */
declare global {
  interface Window { XLSX: any }
}

function useXLSX() {
  const [ready, setReady] = useState(!!window.XLSX)
  useEffect(() => {
    if (window.XLSX) { setReady(true); return }
    const s = document.createElement('script')
    s.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js'
    s.onload = () => setReady(true)
    document.head.appendChild(s)
  }, [])
  return ready ? window.XLSX : null
}

/* ── column mappings per sheet/category ── */
const SHEET_MAP: Record<string, { category: string; columns: Record<string, string> }> = {
  Labor: {
    category: 'labor',
    columns: {
      'Name': 'name',
      'Rate ($/hr)': 'rate',
      'Overtime Rate ($/hr)': 'overtimeRate',
      'Public Works Rate ($/hr)': 'publicWorksRate',
    },
  },
  Equipment: {
    category: 'equipment',
    columns: {
      'Name': 'name',
      'Rate ($/day)': 'rate',
      'Fuel Surcharge (%)': 'fuelCharge',
    },
  },
  // Materials excluded — material items are tied to writeup formulas and
  // auto-created by the material-formulas registry. Editing them here could
  // break the writeup → material calculation pipeline.
  Striping: {
    category: 'striping',
    columns: {
      'Name': 'name',
      'Unit': 'unit',
      'Rate': 'rate',
      'Category': 'stripingCategory',
      'Restripe Cost': 'restripeCost',
      'New Layout +Cost': 'newLayoutAdditionalCost',
      'Double Coat +Cost': 'doubleCoatAdditionalCost',
    },
  },
  Miscellaneous: {
    category: 'miscellaneous',
    columns: {
      'Name': 'name',
      'Unit': 'unit',
      'Rate': 'rate',
    },
  },
}

/* ── defaults per category ── */
const CATEGORY_DEFAULTS: Record<string, Record<string, unknown>> = {
  labor: { unit: 'hr' },
  equipment: { unit: 'day' },
  striping: { unit: 'each' },
  miscellaneous: { unit: 'each' },
}

interface ParsedItem {
  name: string
  category: string
  unit: string
  rate: number
  [key: string]: unknown
}

interface ImportResult {
  total: number
  imported: number
  errors: string[]
}

export function PricingImportPage() {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ParsedItem[]>([])
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const XLSX = useXLSX()

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return

    if (!XLSX) {
      setParseError('Spreadsheet library is still loading. Please try again in a moment.')
      return
    }

    setFile(f)
    setResult(null)
    setParseError(null)

    try {
      const buffer = await f.arrayBuffer()
      const wb = XLSX.read(buffer, { type: 'array' })
      const items: ParsedItem[] = []

      for (const sheetName of wb.SheetNames) {
        const mapping = SHEET_MAP[sheetName]
        if (!mapping) continue

        const ws = wb.Sheets[sheetName]
        const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(ws)

        for (const row of rows) {
          const item: Record<string, unknown> = {
            category: mapping.category,
            ...CATEGORY_DEFAULTS[mapping.category],
          }

          // Map spreadsheet columns to API fields
          for (const [colName, fieldName] of Object.entries(mapping.columns)) {
            if (row[colName] !== undefined && row[colName] !== null && row[colName] !== '') {
              item[fieldName] = row[colName]
            }
          }

          // Skip rows without a name
          if (!item.name || String(item.name).trim() === '') continue

          // Ensure rate is a number
          item.rate = Number(item.rate) || 0

          // Equipment: fuelCharge comes as decimal (0.15), keep as-is for API
          if (mapping.category === 'equipment' && item.fuelCharge !== undefined) {
            item.fuelCharge = Number(item.fuelCharge) || 0
          }

          // Striping: enable add-on flags if costs are set
          if (mapping.category === 'striping') {
            if (Number(item.newLayoutAdditionalCost) > 0) item.newLayoutEnabled = true
            if (Number(item.doubleCoatAdditionalCost) > 0) item.doubleCoatEnabled = true
            item.restripeCost = Number(item.restripeCost) || 0
            item.newLayoutAdditionalCost = Number(item.newLayoutAdditionalCost) || 0
            item.doubleCoatAdditionalCost = Number(item.doubleCoatAdditionalCost) || 0
          }

          // Numeric fields
          for (const numField of ['overtimeRate', 'publicWorksRate']) {
            if (item[numField] !== undefined) {
              item[numField] = Number(item[numField]) || 0
            }
          }

          items.push(item as ParsedItem)
        }
      }

      if (items.length === 0) {
        setParseError('No valid items found. Make sure your spreadsheet has sheets named: Labor, Equipment, Striping, Miscellaneous')
        setPreview([])
        return
      }

      setPreview(items)
    } catch {
      setParseError('Failed to read spreadsheet. Please make sure it is a valid .xlsx file.')
      setPreview([])
    }
  }

  const handleImport = async () => {
    if (preview.length === 0) return

    setImporting(true)
    setResult(null)
    const errors: string[] = []
    let importedCount = 0

    // Group items by category and import each group
    const grouped: Record<string, ParsedItem[]> = {}
    for (const item of preview) {
      if (!grouped[item.category]) grouped[item.category] = []
      grouped[item.category].push(item)
    }

    for (const [category, items] of Object.entries(grouped)) {
      try {
        const result = await pricingService.importItems(items as any)
        importedCount += result.length
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || 'Unknown error'
        errors.push(`${category}: ${msg}`)
      }
    }

    setResult({ total: preview.length, imported: importedCount, errors })
    setImporting(false)
  }

  const handleReset = () => {
    setFile(null)
    setPreview([])
    setResult(null)
    setParseError(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  // Group preview by category for display
  const grouped = preview.reduce<Record<string, ParsedItem[]>>((acc, item) => {
    if (!acc[item.category]) acc[item.category] = []
    acc[item.category].push(item)
    return acc
  }, {})

  return (
    <div>
      <div className="est-list-head">
        <h1 className="est-list-title">Import / Export Pricing</h1>
      </div>

      <p className="lch-page-desc">
        Download the pricing template, fill in your rates, and upload to bulk-import Labor, Equipment, Striping,
        and Miscellaneous items. Existing items with the same name and category will be updated.
        Materials are managed separately since they are linked to estimate writeup formulas.
      </p>

      {/* Download & Upload cards */}
      <div className="pi-cards">
        {/* Download Template */}
        <div className="card pi-card">
          <div className="pi-card-icon">
            <DownloadIcon />
          </div>
          <h3 className="pi-card-title">Download Template</h3>
          <p className="pi-card-desc">
            Get the Excel template with sheets for each pricing category.
            Fill in your items and rates, then upload below.
          </p>
          <a
            href="/templates/paver-pricing-template.xlsx"
            download="paver-pricing-template.xlsx"
            className="btn pi-btn-download"
          >
            <DownloadIcon /> Download Template
          </a>
        </div>

        {/* Upload */}
        <div className="card pi-card">
          <div className="pi-card-icon">
            <UploadIcon />
          </div>
          <h3 className="pi-card-title">Upload Pricing</h3>
          <p className="pi-card-desc">
            Upload your completed spreadsheet to import all pricing items.
            Items are matched by name — new items are created, existing ones updated.
          </p>
          <label className="btn pi-btn-upload">
            <UploadIcon /> {file ? file.name : 'Choose File'}
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="pi-file-input"
            />
          </label>
        </div>
      </div>

      {/* Parse error */}
      {parseError && (
        <div className="pi-error">{parseError}</div>
      )}

      {/* Preview */}
      {preview.length > 0 && !result && (
        <div className="card pi-preview">
          <div className="pi-preview-head">
            <h3>Preview — {preview.length} items found</h3>
            <div className="pi-preview-actions">
              <button className="btn btn-ghost" onClick={handleReset}>Cancel</button>
              <button className="btn" onClick={handleImport} disabled={importing}>
                {importing ? 'Importing...' : `Import ${preview.length} Items`}
              </button>
            </div>
          </div>

          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="pi-preview-group">
              <h4 className="pi-preview-category">{category} ({items.length})</h4>
              <table className="pi-preview-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Unit</th>
                    <th>Rate</th>
                    {category === 'labor' && <th>Overtime</th>}
                    {category === 'labor' && <th>Public Works</th>}
                    {category === 'equipment' && <th>Fuel %</th>}
                    {category === 'striping' && <th>Category</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr key={i}>
                      <td>{item.name}</td>
                      <td>{item.unit}</td>
                      <td>${Number(item.rate).toFixed(2)}</td>
                      {category === 'labor' && <td>${Number(item.overtimeRate || 0).toFixed(2)}</td>}
                      {category === 'labor' && <td>${Number(item.publicWorksRate || 0).toFixed(2)}</td>}
                      {category === 'equipment' && <td>{((Number(item.fuelCharge) || 0) * 100).toFixed(0)}%</td>}
                      {category === 'striping' && <td>{String(item.stripingCategory || '—')}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`card pi-result ${result.errors.length > 0 ? 'pi-result-warn' : 'pi-result-success'}`}>
          <div className="pi-result-icon">
            {result.errors.length > 0 ? <WarnIcon /> : <CheckIcon />}
          </div>
          <div>
            <h3 className="pi-result-title">
              {result.errors.length > 0
                ? `Imported ${result.imported} of ${result.total} items`
                : `Successfully imported ${result.imported} items`
              }
            </h3>
            {result.errors.length > 0 && (
              <ul className="pi-result-errors">
                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
            <button className="btn btn-ghost pi-result-reset" onClick={handleReset}>Import Another File</button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ── Inline SVG icons ── */
function DownloadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

function UploadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  )
}

function WarnIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  )
}
